import {BlockedIcon} from '@primer/octicons-react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {getGroupCreatedAt} from '../utils/get-group-created-at'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {BlockingRemovedEvent$key} from './__generated__/BlockingRemovedEvent.graphql'
import type {BaseDependencyEventProps} from './BlockedByAddedEvent'
import styles from './DependencyEvent.module.css'
import {IssueLink} from './IssueLink'
import {Ago} from './row/Ago'
import {TimelineRow} from './row/TimelineRow'

type EventItem = BlockingRemovedEvent$key & {createdAt?: string}

type BlockingRemovedEventProps = BaseDependencyEventProps & {
  queryRef: EventItem
  rollupGroup?: Record<'BlockingRemovedEvent', EventItem[]>
}

export const BlockingRemovedEventFragment = graphql`
  fragment BlockingRemovedEvent on BlockingRemovedEvent {
    databaseId
    actor {
      ...TimelineRowEventActor
    }
    createdAt
    blockedIssue {
      ...IssueLink
    }
  }
`

export function BlockingRemovedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
  repositoryId,
  ownerLogin,
}: BlockingRemovedEventProps) {
  const {actor, createdAt, blockedIssue, databaseId} = useFragment(BlockingRemovedEventFragment, queryRef)

  if (!blockedIssue) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId
  const rolledUpGroup = rollupGroup?.BlockingRemovedEvent || []
  const itemsToRender = rolledUpGroup.length === 0 ? [queryRef] : rolledUpGroup
  const eventCreatedAt = getGroupCreatedAt(queryRef.createdAt, rolledUpGroup)

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      showAgoTimestamp={false}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={BlockedIcon}
    >
      <TimelineRow.Main>
        <span>
          {itemsToRender.length === 1
            ? LABELS.timeline.blockingRemoved.single
            : LABELS.timeline.blockingRemoved.multiple(itemsToRender.length)}
        </span>{' '}
        {eventCreatedAt ? (
          <Ago timestamp={new Date(eventCreatedAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        ) : null}
      </TimelineRow.Main>
      <TimelineRow.Secondary>
        <ul className={styles.issueDependenciesList}>
          {itemsToRender.map((item, index) => (
            <BlockingRemovedEventItem
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${databaseId}_${index}`}
              event={item}
              targetRepositoryId={repositoryId}
              targetOwnerLogin={ownerLogin}
            />
          ))}
        </ul>
      </TimelineRow.Secondary>
    </TimelineRow>
  )
}

function BlockingRemovedEventItem({
  event,
  targetRepositoryId,
  targetOwnerLogin,
}: {
  event: BlockingRemovedEvent$key
  targetRepositoryId: string
  targetOwnerLogin: string
}) {
  const {blockedIssue} = useFragment(BlockingRemovedEventFragment, event)

  if (!blockedIssue) {
    return null
  }

  return (
    <li className={styles.issueDependencyItem}>
      <IssueLink data={blockedIssue} targetRepositoryId={targetRepositoryId} targetOwnerLogin={targetOwnerLogin} />
    </li>
  )
}

try{ BlockingRemovedEvent.displayName ||= 'BlockingRemovedEvent' } catch {}
try{ BlockingRemovedEventItem.displayName ||= 'BlockingRemovedEventItem' } catch {}